import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useToaster } from "core/hooks/useToaster";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { useSWRConfig } from "swr";
import type { PaymentRequestUpdateDto } from "../entities";
import { usePaymentRequestStatusHistory } from "./usePaymentRequestStatusHistory";

export function usePaymentRequest() {
  const {
    query: { paymentRequestId },
  } = useRouter();
  const { errorToast } = useToaster();
  const { t } = useTranslation("PaymentRequest", {
    keyPrefix: "Next:PaymentRequest:usePaymentRequest",
  });
  const { mutate: globalMutate } = useSWRConfig();
  const centrixFetch = useCentrixFetch();
  const { mutate: mutateStatusHistory, addLocalStatusHistoryEntry } =
    usePaymentRequestStatusHistory(paymentRequestId);

  const {
    data,
    isError,
    isLoading,
    mutate: mutatePaymentRequest,
  } = useCentrixApi({
    path: "/api/app/payment-request/{id}",
    parameters: {
      path: {
        id: typeof paymentRequestId === "string" ? paymentRequestId : "",
      },
    },
  });

  if (typeof paymentRequestId !== "string") {
    throw new Error(
      "this hook can only be used on a page with [paymentRequestId]"
    );
  }

  const updateErrorMessage = t("updateErrorMessage");

  async function updatePaymentRequest(
    fields: PaymentRequestUpdateDto & { comment?: string | null | undefined }
  ) {
    if (typeof paymentRequestId !== "string") {
      return;
    }
    const shouldUpdateStatus = fields.status && fields.status !== data?.status;
    if (shouldUpdateStatus && fields && fields.status) {
      addLocalStatusHistoryEntry(fields.status);
    }

    const patchRes = await centrixFetch({
      method: "patch",
      path: "/api/app/payment-request/{id}",
      parameters: { path: { id: paymentRequestId } },
      body: fields as any, // TODO there's a weird type coming form the generated schema, we should probably just switch to a PUT instead of PATCH
      contentType: "application/merge-patch+json",
    });

    if (!patchRes.ok) {
      errorToast(updateErrorMessage);
      return;
    }

    const mutatePromises: Promise<any>[] = [mutatePaymentRequest()];

    await mutatePaymentRequest();
    if (shouldUpdateStatus) {
      mutatePromises.push(mutateStatusHistory());
    }
    if (fields.comment) {
      // using global mutate to avoid a dependency loop between usePaymentRequest and usePaymentRequestComments
      mutatePromises.push(
        globalMutate((cacheKey) => {
          if (typeof cacheKey !== "string") {
            return false;
          }
          if (!cacheKey.includes("payment-request-comment")) {
            return false;
          }
          if (!cacheKey.includes(paymentRequestId)) {
            return false;
          }
          return true;
        })
      );
    }
    await Promise.all(mutatePromises);
  }

  return {
    data,
    isError,
    isLoading,
    mutate: mutatePaymentRequest,
    updatePaymentRequest,
  };
}
